import React, {
  FC, useMemo, useState, useEffect,
} from 'react';
import _ from 'lodash';
import {
  Progress, DropdownItem,
} from 'reactstrap';
import moment from 'moment';
import { getTimeRange, parseAmount } from 'app/utils/parsers';
import Isvg from 'react-inlinesvg';
import history from 'app/routes/history';
import Table from 'app/components/general/Table';
import Button from 'app/components/general/Button';
import ActionDots from 'app/components/general/ActionDots';
import PlusIcon from 'app/images/icons/plus.svg';
import StatusBadge from 'app/components/general/StatusBadge';

import { getTeamPaymentRequestSummary } from 'app/ducks/teamPaymentRequests/selectors';
import { ITeamPaymentRequest } from 'app/ducks/teamPaymentRequests/types';

interface IProps {
  actions: any;
  teamPaymentRequestsById: ITeamPaymentRequest[];
  teamPaymentRequestIds: string[];
  teamId?: string;
  headerTitle?: string;
  appReady?: boolean;
  isLoading?: boolean;
  manual?: boolean;
  saveFilters?: boolean;
  initialFilterConfig?: any;
  persistentFilterConfig?: any;
  pagination?: any;
  reminders?: any;
  createReminder: Function;
  selectTeamPaymentRequest: (value) => void;
}

const PaymentRequestsTable: FC<IProps> = (props: IProps = {
  manual: true,
  actions: undefined,
  teamPaymentRequestsById: undefined,
  teamPaymentRequestIds: undefined,
  initialFilterConfig: undefined,
  persistentFilterConfig: undefined,
  isLoading: false,
  pagination: undefined,
  reminders: undefined,
  createReminder: undefined,
  selectTeamPaymentRequest: undefined,
}) => {
  const filterOptions = [
    {
      label: 'Description',
      accessor: 'description',
      type: 'text',
    },
    {
      label: 'Status',
      accessor: 'status',
      type: 'select',
      options: [
        { id: 'pending', name: 'Collecting' },
        { id: 'collected', name: 'Paid' },
        { id: 'cancelled', name: 'Cancelled' },
      ],
    },
  ];

  const fetchDataFromApi = ({
    sort, filter, pageNumber, pageSize,
  }): void => {
    const payload = {
      page: {
        size: pageSize,
        number: pageNumber,
      },
      sort,
      filter,
    };
    props.actions.getAllTeamPaymentRequests(payload);
  };

  const handleRequestPayment = (): void => {
    history.push('/payment-requests/new');
  };

  const handleClick = (id: string): void => {
    // props.actions.selectTeamPaymentRequest(id);
    history.push(`/payment-requests/detail/${id}`);
  };

  const data = useMemo(() => {
    if (_.isEmpty(props.teamPaymentRequestsById) || props.teamPaymentRequestIds === undefined) {
      return [];
    }
    const rows = _.reduce(
      props.teamPaymentRequestIds,
      (obj, id) => {
        if (!_.isEmpty(props.teamPaymentRequestsById[id])) {
          obj.push(getTeamPaymentRequestSummary(props.teamPaymentRequestsById[id]));
        }
        return obj;
      },
      [],
    );
    return rows;
  }, [props.teamPaymentRequestsById, props.teamPaymentRequestIds]);

  const renderStatus = (cellProps: {
    value: string;
  }): React.ReactNode => (
    <StatusBadge
      paymentText={cellProps.value}
      tick={false}
      className="no-margin-right"
      formattedPaymentText={cellProps.value === 'pending' ? 'Collecting' : false}
    />
  );

  const renderDate = (cellProps: {
    value: string;
  }): string => (_.isEmpty(cellProps.value) ? '' : moment(cellProps.value).format('D MMM YYYY'));

  const renderDescription = (cellProps: {
    value: {
      description: string;
      numberOfTeamMembers: number;
    };
  }): React.ReactNode => {
    const proNoun = cellProps.value.numberOfTeamMembers > 1 ? 'people' : 'person';
    return (
      <span className="d-inline">
        {cellProps.value.description}
        <span className="margin-left-5 text-weight-medium-bold">
          {`(${cellProps.value.numberOfTeamMembers.toString()} ${proNoun})`}
        </span>
      </span>
    );
  };

  const renderProgress = (cellProps: {
    value: {
      collected: number;
      total: number;
    };
  }): React.ReactNode => {
    const progressPercentage = cellProps.value.collected === 0
      ? 0
      : (cellProps.value.collected / cellProps.value.total) * 100;

    return (
      <div className="d-flex align-items-center">
        <div>
          <Progress multi className="progress-in-table with-margin-right-5">
            <Progress bar color="green" value={progressPercentage} />
            <Progress bar color="grey" value={100 - progressPercentage} />
          </Progress>
        </div>
        <div>
          <span className="d-inline text-weight-medium-bold">{parseAmount(cellProps.value.collected)}</span>
          {' '}
          of
          {' '}
          {parseAmount(cellProps.value.total)}
        </div>
      </div>
    );
  };

  const handleReminder = (event, teamPaymentRequestId): void => {
    event.stopPropagation();
    if (!props?.reminders?.isLoading) {
      props.createReminder({
        paymentRequests: [
          {
            id: teamPaymentRequestId,
          },
        ],
      });
    }
  };

  const renderDropdownOptions = (pc: {
    teamPaymentRequestId: string;
    pendingTeamMembers: number;
  }): React.ReactNode => (
    <div>
      {pc.pendingTeamMembers !== 0 && (
        <DropdownItem
          onClick={(e): void => {
            handleReminder(e, pc?.teamPaymentRequestId);
          }}
        >
          Send Reminders
        </DropdownItem>
      )}
    </div>
  );

  const renderActionDots = (cellProps: {
    value: {
      teamPaymentRequestId: string;
      pendingTeamMembers: number;
    };
  }): React.ReactNode => (
    <ActionDots id={cellProps?.value?.teamPaymentRequestId}>
      {renderDropdownOptions({
        teamPaymentRequestId: cellProps.value.teamPaymentRequestId,
        pendingTeamMembers: cellProps.value.pendingTeamMembers,
      })}
    </ActionDots>
  );

  const tinyColumn = 'col col-3 col-md-2 col-lg-1';
  const smallColumn = 'col col-3 col-lg-2';
  const growColumn = 'col d-flex';
  const mediumColumn = 'col col-4 col-md-3 col-lg-3 col-xl-2';
  const largeColumn = 'col col-4 col-md-4 col-lg-4 col-xl-3';

  const columns = useMemo(() => [
    {
      Header: 'Description',
      accessor: 'summary',
      className: growColumn,
      headerClassName: growColumn,
      Cell: renderDescription,
      sortType: (rowA, rowB, id, desc): number => {
        if (rowA.original.summary.description > rowB.original.summary.description) return -1;
        if (rowB.original.summary.description > rowA.original.summary.description) return 1;
        return 0;
      },
    },
    {
      id: 'status',
      Header: 'Status',
      className: mediumColumn,
      headerClassName: mediumColumn,
      accessor: 'status',
      Cell: renderStatus,
    },
    {
      id: 'collectionProgress',
      Header: 'Collected',
      className: `${largeColumn} d-none d-lg-flex`,
      headerClassName: `${largeColumn} d-none d-lg-flex`,
      accessor: 'collected',
      Cell: renderProgress,
      sortType: (rowA, rowB, id, desc): number => {
        const percentageA = rowA.original.collected.collected / rowA.original.collected.total;
        const percentageB = rowB.original.collected.collected / rowB.original.collected.total;
        if (percentageA > percentageB) return -1;
        if (percentageB > percentageA) return 1;
        return 0;
      },
    },
    {
      Header: 'Date Requested',
      accessor: 'createdAt',
      className: `${smallColumn} d-none d-lg-flex`,
      headerClassName: `${smallColumn} d-none d-lg-flex`,
      sortType: 'datetime',
      Cell: renderDate,
    },
    {
      id: 'actionCol',
      Header: 'Actions',
      className: `${tinyColumn} d-none d-md-flex`,
      headerClassName: `${tinyColumn} d-none d-md-flex`,
      accessor: (row): any => ({
        teamPaymentRequestId: row.id,
        pendingTeamMembers: row.pendingTeamMembers,
      }),
      disableSortBy: true,
      Cell: renderActionDots,
    },
  ], []);

  const counterText = props.pagination.meta.totalCount === 1 ? '1 payment request' : `${props.pagination.meta.totalCount || 0} payment requests`;

  // const RenderTable = React.memo((props: IProps) => (
  //   <Table
  //     data={data}
  //     columns={columns}
  //     isLoading={props.isLoading}
  //     aggressiveLoader
  //     headerTitle={props.headerTitle}
  //     objectTitle="payment requests"
  //     filterTitle="Payment Requests Data"
  //     counterText={counterText}
  //     handleClick={handleClick}
  //     filterable
  //     saveFilters={props.saveFilters}
  //     manual={props.manual}
  //     fetchDataFromApi={fetchDataFromApi}
  //     filterOptions={filterOptions}
  //     filterConfig={props.initialFilterConfig}
  //     persistentFilterConfig={props.persistentFilterConfig}
  //     pagination={props.pagination}
  //     showPagination
  //   >
  //     <Button onClick={handleRequestPayment} className="btn-thinner btn-2">
  //       <Isvg src={PlusIcon} className="btn-icon" />
  //       New Payment Request
  //     </Button>
  //   </Table>
  // ));

  return (
    <Table
      data={data}
      columns={columns}
      isLoading={props.isLoading}
      aggressiveLoader={false}
      headerTitle={props.headerTitle}
      objectTitle="payment requests"
      filterTitle="Payment Requests Data"
      counterText={counterText}
      handleClick={handleClick}
      filterable
      saveFilters={props.saveFilters}
      manual={props.manual}
      fetchDataFromApi={fetchDataFromApi}
      filterOptions={filterOptions}
      filterConfig={props.initialFilterConfig}
      persistentFilterConfig={props.persistentFilterConfig}
      pagination={props.pagination}
      showPagination
    >
      <Button noLoading onClick={handleRequestPayment} className="btn-thinner btn-2">
        <Isvg src={PlusIcon} className="btn-icon" />
        New Payment Request
      </Button>
    </Table>
  );
};

export default PaymentRequestsTable;
